import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConnectorRegistryEntry,
  ConnectorEnableStatus,
  connectorsAPI,
} from '../../services/api';

/**
 * ManageConnectionConnectingState — universal-key (server-held API key)
 * lifecycle UI for the manage modal. Replaces the OAuth popup flow with
 * a one-click Enable / Disable. Wave 2 of the HeyGen migration.
 *
 * Drives off GET /api/connectors/:id/status — fetched on mount and
 * re-read after every enable/disable so the UI reflects the server.
 *
 *   ┌─ not_enabled ─────────────────────────┐
 *   │  [icon] HeyGen   MCP                   │
 *   │  Make AI videos with avatars and       │
 *   │  voices...                              │
 *   │  With HeyGen MCP, you can: …           │
 *   │                  [ Enable HeyGen ]      │
 *   └────────────────────────────────────────┘
 *
 *   ┌─ enabled ─────────────────────────────┐
 *   │  ● Enabled · since Apr 23              │
 *   │  …features…                            │
 *   │                          [ Disable ]   │  ← subtle, not destructive
 *   └────────────────────────────────────────┘
 *
 *   ┌─ disabled ────────────────────────────┐
 *   │  ○ Disabled · on May 3                 │
 *   │                       [ Re-enable ]    │
 *   └────────────────────────────────────────┘
 *
 * Error UX: server responses include { error, userMessage } where
 * `userMessage` is plain-English copy safe for a non-technical audience.
 * We surface `userMessage` only — never the raw `error` code.
 *
 * The OAuth popup flow that used to live here has been retired in favour
 * of the universal-key flow (Wave 2). Existing OAuth server routes stay
 * in place until Wave 3 deletes them.
 */

const CONNECTOR_COPY: Record<string, { description: string; features: string[] }> = {
  heygen: {
    description:
      "Make AI videos with avatars and voices. Once enabled, ask your AI coworker to record a script with a chosen avatar, synthesize speech, or translate a video — it'll call HeyGen directly and drop the result into Files.",
    features: [
      'Generate videos from a script with an AI avatar of your choice',
      'Synthesize speech in natural voices across many languages',
      'Translate an existing video into another language with lip sync preserved',
      'Reuse or extend videos you generated earlier',
    ],
  },
};

export interface ManageConnectionConnectingStateProps {
  entry: ConnectorRegistryEntry;
  // Called after a successful enable so the parent can refresh its
  // shared connections list (the overview's "Enabled" badge etc.).
  // Optional — when omitted the component still works standalone.
  onChange?: () => void | Promise<void>;
}

interface InlineAlert {
  // kind drives the visual tone — `warn` for 503-style "try again later"
  // states, `error` for hard failures, `info` for premium/upgrade CTAs.
  kind: 'warn' | 'error' | 'info';
  text: string;
  // Optional CTA — used today for the 403/premium upgrade path. The
  // server isn't returning an upgrade URL yet, but the slot is here so
  // the day it does, the wiring is one line.
  ctaLabel?: string;
  ctaHref?: string;
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

const ManageConnectionConnectingState: React.FC<ManageConnectionConnectingStateProps> = ({
  entry,
  onChange,
}) => {
  const [status, setStatus] = useState<ConnectorEnableStatus | null>(null);
  const [enabledAt, setEnabledAt] = useState<string | null>(null);
  const [disabledAt, setDisabledAt] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [alert, setAlert] = useState<InlineAlert | null>(null);

  const primaryRef = useRef<HTMLButtonElement | null>(null);

  // Hydrate from GET /status on mount. Treat any failure as "not_enabled"
  // so the user still sees the Enable CTA — better than a blank panel
  // when the status route is briefly unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await connectorsAPI.getStatus(entry.id);
        if (cancelled) return;
        setStatus(res.data.status);
        setEnabledAt(res.data.enabledAt);
        setDisabledAt(res.data.disabledAt);
      } catch {
        if (!cancelled) setStatus('not_enabled');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  // Focus the primary CTA once we know which one to render so a keyboard
  // user can hit Enter to enable / disable / re-enable immediately.
  useEffect(() => {
    if (status && primaryRef.current) primaryRef.current.focus();
  }, [status]);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await connectorsAPI.getStatus(entry.id);
      setStatus(res.data.status);
      setEnabledAt(res.data.enabledAt);
      setDisabledAt(res.data.disabledAt);
    } catch {
      /* non-fatal; current UI stays as-is */
    }
  }, [entry.id]);

  const surfaceError = useCallback((err: any, fallback: string) => {
    // Server uses { error, userMessage } — surface userMessage only.
    // Raw error codes are diagnostic; users see plain English.
    const body = err?.response?.data;
    const httpStatus = err?.response?.status;
    if (body?.userMessage) {
      // 403 — premium gate. Render as info with a (future) upgrade CTA.
      if (httpStatus === 403) {
        setAlert({
          kind: 'info',
          text: body.userMessage,
          ctaLabel: body.upgradeUrl ? 'Upgrade' : undefined,
          ctaHref: body.upgradeUrl,
        });
        return;
      }
      // 503 — temporarily unavailable / not configured. Soft warn tone.
      if (httpStatus === 503) {
        setAlert({ kind: 'warn', text: body.userMessage });
        return;
      }
      setAlert({ kind: 'error', text: body.userMessage });
      return;
    }
    setAlert({ kind: 'error', text: fallback });
  }, []);

  const handleEnable = useCallback(async () => {
    setPending(true);
    setAlert(null);
    try {
      const res = await connectorsAPI.enable(entry.id);
      setStatus(res.data.status);
      setEnabledAt(res.data.enabledAt || null);
      setDisabledAt(null);
      if (onChange) await onChange();
    } catch (err: any) {
      surfaceError(err, "Couldn't enable this connector. Try again.");
    } finally {
      setPending(false);
    }
  }, [entry.id, onChange, surfaceError]);

  const handleDisable = useCallback(async () => {
    setPending(true);
    setAlert(null);
    try {
      const res = await connectorsAPI.disable(entry.id);
      setStatus(res.data.status);
      setDisabledAt(res.data.disabledAt || new Date().toISOString());
      if (onChange) await onChange();
      // Re-read so timestamps reflect the canonical server values.
      await refreshStatus();
    } catch (err: any) {
      surfaceError(err, "Couldn't disable this connector. Try again.");
    } finally {
      setPending(false);
    }
  }, [entry.id, onChange, refreshStatus, surfaceError]);

  const copy = CONNECTOR_COPY[entry.id];

  // Render loading-shaped placeholder during the initial /status fetch
  // so we don't briefly flash the "Enable" CTA before resolving to the
  // "Disable" CTA for an already-enabled user.
  if (status === null) {
    return (
      <div className="px-6 sm:px-7 pb-7" data-testid="manage-connecting-state">
        <div className="pt-4 h-24 animate-pulse">
          <div className="h-3 w-3/4 rounded bg-white/5" />
          <div className="mt-2 h-3 w-1/2 rounded bg-white/5" />
        </div>
      </div>
    );
  }

  const isEnabled = status === 'enabled';
  const isDisabled = status === 'disabled';
  const enabledLabel = enabledAt ? formatDateShort(enabledAt) : '';
  const disabledLabel = disabledAt ? formatDateShort(disabledAt) : '';

  return (
    <div className="px-6 sm:px-7 pb-7" data-testid="manage-connecting-state">
      {/* Status badge — shown for enabled/disabled states. The header
          badge in the parent panel only knows about OAuth "connected";
          we render a universal-key-aware badge inline here. */}
      {isEnabled && (
        <div
          className="mt-4 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium"
          data-testid="universal-enabled-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
          Enabled{enabledLabel ? ` · since ${enabledLabel}` : ''}
        </div>
      )}
      {isDisabled && (
        <div
          className="mt-4 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs font-medium"
          data-testid="universal-disabled-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white/40" aria-hidden="true" />
          Disabled{disabledLabel ? ` · on ${disabledLabel}` : ''}
        </div>
      )}

      {copy?.description && (
        <p className="text-sm text-white/80 leading-relaxed pt-4">{copy.description}</p>
      )}

      {copy?.features && copy.features.length > 0 && (
        <>
          <h3
            className="mt-6 text-sm font-semibold text-white"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            With {entry.displayName}, you can:
          </h3>
          <ul className="mt-2 space-y-1.5 text-sm text-white/70">
            {copy.features.map((f, i) => (
              <li key={i} className="flex gap-2">
                <span aria-hidden="true" className="text-white/40 flex-shrink-0">
                  •
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* all_or_nothing inline note — replaces the per-tool permission grid
          that per_tool connectors render in ManageConnectionPanel. For
          universal-key connectors there's no security boundary the user
          can carve out (we own the API key) and a 40-row toggle list is
          unkind UX for a non-technical audience. Only render when the
          connector is enabled — disabled / not_enabled states already
          show the Enable CTA which carries the same semantics. */}
      {isEnabled && entry.permissionMode === 'all_or_nothing' && (
        <p
          className="mt-5 text-xs text-white/55 leading-relaxed"
          data-testid="universal-all-or-nothing-note"
        >
          All {entry.displayName} tools are available when this connector is enabled.
        </p>
      )}

      {/* Inline alert — server userMessage only. Never the raw error code. */}
      {alert && (
        <div
          role="alert"
          data-testid="universal-alert"
          className={[
            'mt-5 px-3 py-2 rounded-md text-sm border',
            alert.kind === 'warn'
              ? 'bg-amber-500/10 text-amber-200 border-amber-500/30'
              : alert.kind === 'info'
                ? 'bg-sky-500/10 text-sky-200 border-sky-500/30'
                : 'bg-red-500/10 text-red-200 border-red-500/30',
          ].join(' ')}
        >
          <div>{alert.text}</div>
          {alert.ctaLabel && alert.ctaHref && (
            <a
              href={alert.ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="universal-alert-cta"
              className="inline-flex mt-2 text-xs font-semibold underline hover:no-underline"
            >
              {alert.ctaLabel} →
            </a>
          )}
        </div>
      )}

      {/* Primary action row — three button shapes for the three states. */}
      <div className="mt-7 flex justify-end items-center gap-2">
        {isEnabled ? (
          <button
            ref={primaryRef}
            type="button"
            onClick={handleDisable}
            disabled={pending}
            data-testid="manage-disable"
            // Subtle styling: this is a benign flip, not a destructive
            // disconnect. Keep it quiet — the user opted in once and
            // shouldn't be punished visually for toggling off.
            className="inline-flex items-center px-4 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-60"
          >
            {pending ? 'Disabling…' : 'Disable'}
          </button>
        ) : (
          <button
            ref={primaryRef}
            type="button"
            onClick={handleEnable}
            disabled={pending}
            data-testid="manage-enable"
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
          >
            {pending
              ? isDisabled
                ? 'Re-enabling…'
                : 'Enabling…'
              : isDisabled
                ? `Re-enable ${entry.displayName}`
                : `Enable ${entry.displayName}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default ManageConnectionConnectingState;
